import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// `kind` is a data code ("alarm" | "anomali") produced by Home.js; only the labels are translated.
const FILTERS = [
  { value: "all", labelKey: "home.feed.filterAll" },
  { value: "alarm", labelKey: "home.feed.filterAlarm" },
  { value: "anomali", labelKey: "home.feed.filterAnomaly" },
];

/**
 * Alarm + anomaly events in one time-ordered feed.
 * events: [{ kind: "alarm" | "anomali", rawTime, time, field, level, value, detail }]
 */
export default function EventFeedWidget({ title, events = [] }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const rows = events.filter((e) => filter === "all" || e.kind === filter);
  const count = (k) => events.filter((e) => k === "all" || e.kind === k).length;

  return (
    <div style={styles.root}>
      <div style={styles.head}>
        <span style={styles.title} title={title}>{title}</span>
        <div style={styles.filters} role="group" aria-label={t("home.feed.aria")}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
              style={{ ...styles.chip, ...(filter === f.value ? styles.chipOn : null) }}
            >
              {t(f.labelKey)} {count(f.value)}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div style={styles.empty}>{t("home.feed.empty")}</div>
      ) : (
        <ul style={styles.list}>
          {rows.map((e, i) => (
            <li key={`${e.kind}-${e.rawTime}-${i}`} style={styles.item}>
              <span
                style={{ ...styles.dot, background: e.kind === "alarm" ? "var(--vx-danger)" : "var(--vx-anomaly)" }}
                aria-hidden="true"
              />
              <span style={styles.kind}>{e.kind === "alarm" ? t("home.feed.filterAlarm") : t("home.feed.filterAnomaly")}</span>
              <span style={styles.field}>{e.field}</span>
              <span style={styles.detail}>{e.detail}</span>
              <span style={styles.value}>{e.value}</span>
              <span style={styles.time}>{e.time}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const styles = {
  root: { height: "100%", display: "flex", flexDirection: "column", minHeight: 0, fontFamily: "var(--vx-font-sans)", background: "var(--vx-surface)" },
  head: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 12px", borderBottom: "1px solid var(--vx-line-soft)" },
  title: { fontSize: 13, fontWeight: 700, color: "var(--vx-text)", marginRight: "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  filters: { display: "flex", gap: 4 },
  chip: { font: "inherit", fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999, border: "1px solid var(--vx-line)", background: "var(--vx-surface)", color: "var(--vx-text-2)", cursor: "pointer" },
  chipOn: { background: "var(--vx-accent-soft)", borderColor: "var(--vx-accent-line)", color: "var(--vx-accent-strong)" },
  list: { listStyle: "none", margin: 0, padding: 0, overflowY: "auto", flex: 1, minHeight: 0 },
  item: { display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderBottom: "1px solid var(--vx-line-soft)", fontSize: 13, color: "var(--vx-text)" },
  dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  kind: { fontWeight: 600, minWidth: 54 },
  field: { fontFamily: "var(--vx-font-mono)", fontSize: 12 },
  detail: { flex: 1, color: "var(--vx-text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  value: { fontFamily: "var(--vx-font-mono)", fontSize: 12 },
  time: { fontFamily: "var(--vx-font-mono)", fontSize: 11, color: "var(--vx-text-2)", whiteSpace: "nowrap" },
  empty: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--vx-text-2)", fontSize: 13 },
};
